import { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { blogArticles } from '@/mocks/blog';

export default function ArticlePage() {
  const location = useLocation();
  const slug = location.pathname.replace('/', '');
  const article = blogArticles.find((a) => a.slug === slug) || blogArticles[0];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: '2026-06-27',
    dateModified: '2026-06-27',
    author: {
      '@type': 'Organization',
      name: 'itaobuy.hair',
      url: 'https://itaobuy.hair/',
    },
    publisher: {
      '@type': 'Organization',
      name: 'itaobuy.hair',
      url: 'https://itaobuy.hair/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/62f99c0415358bd5388d5ed1df9ac1ff.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://itaobuy.hair/${slug}`,
    },
  };

  const faqSchema = article.slug === 'itaobuy-faq' ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: article.content.sections.map((section) => ({
      '@type': 'Question',
      name: section.heading,
      acceptedAnswer: {
        '@type': 'Answer',
        text: section.body,
      },
    })),
  } : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  if (!article.content) {
    return (
      <>
        <SchemaOrg data={articleSchema} />
        <Navbar />
        <main className="min-h-screen pt-24 pb-16 px-6 md:px-10">
          <div className="max-w-[800px] mx-auto text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-[13px] text-foreground-600 hover:text-foreground-950 transition-colors mb-10"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>

            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="text-[10px] font-semibold text-accent-700 uppercase tracking-wider bg-accent-100 px-2.5 py-1 rounded-full">
                {article.category}
              </span>
              <span className="text-[11px] text-foreground-500">{article.date}</span>
              <span className="text-[11px] text-foreground-500">&middot; {article.readTime}</span>
            </div>

            <h1 className="text-[28px] md:text-[36px] font-heading text-foreground-950 leading-[1.2] mb-5">
              {article.title}
            </h1>
            <p className="text-[14px] text-foreground-600 max-w-[560px] mx-auto leading-relaxed mb-10">
              {article.summary}
            </p>

            <div className="bg-background-100 rounded-2xl p-10">
              <p className="text-[14px] text-foreground-600 mb-6">
                This article is being prepared. Check back soon for the full guide.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-[13px] font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                >
                  Explore itaobuy sheet
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
                <Link
                  to="/itaobuy-spreadsheet-guide"
                  className="px-6 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-[13px] font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Read the Full Guide
                </Link>
              </div>
            </div>

            <div className="mt-10">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-[12px] text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <i className="ri-home-4-line"></i>
                Return to itaobuy.hair Homepage
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const { content } = article;

  return (
    <>
      <SchemaOrg data={articleSchema} />
      {faqSchema && <SchemaOrg data={faqSchema} />}
      <Navbar />
      <main>
        <article className="pt-24 pb-16 px-6 md:px-10">
          <div className="max-w-[800px] mx-auto">
            <div className="mb-10">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-[13px] text-foreground-600 hover:text-foreground-950 transition-colors mb-8"
              >
                <i className="ri-arrow-left-line"></i>
                Back to Homepage
              </Link>

              <div className="flex items-center gap-3 mb-4">
                <span className="text-[10px] font-semibold text-accent-700 uppercase tracking-wider bg-accent-100 px-2.5 py-1 rounded-full">
                  {article.category}
                </span>
                <span className="text-[11px] text-foreground-500">{article.date}</span>
                <span className="text-[11px] text-foreground-500">&middot; {article.readTime}</span>
              </div>

              <h1 className="text-[28px] md:text-[38px] font-heading text-foreground-950 leading-[1.2] mb-6">
                {article.title}
              </h1>

              <p className="text-[15px] text-foreground-600 leading-relaxed">
                {article.summary}
              </p>
            </div>

            <div className="w-full h-[360px] rounded-2xl overflow-hidden mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20laptop%20displaying%20organized%20spreadsheet%20data%2C%20natural%20light%20from%20window%2C%20neutral%20beige%20cream%20tones%2C%20editorial%20photography%2C%20professional%20environment%2C%20calm%20aesthetic%20composition&width=1600&height=720&seq=article-hero-v4&orientation=landscape"
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom">
              <p className="text-[15px] text-foreground-700 leading-relaxed mb-8">
                {content.intro}
              </p>

              {content.sections.map((section, index) => (
                <div key={index} className="mb-10">
                  <h2 className="text-[20px] md:text-[24px] font-heading text-foreground-950 font-semibold leading-snug mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-[14px] text-foreground-700 leading-relaxed">
                    {section.body}
                  </p>
                </div>
              ))}

              <div className="bg-secondary-50 border border-secondary-200/50 rounded-2xl p-8 my-12">
                <h3 className="text-[16px] font-heading text-foreground-950 font-semibold mb-3">Key Takeaways</h3>
                <ul className="space-y-2.5">
                  {content.sections.map((section, index) => (
                    <li key={index} className="flex items-start gap-2.5 text-[13px] text-foreground-700">
                      <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                      <span>
                        <strong className="text-foreground-900">{section.heading}:</strong> {section.body.slice(0, 120)}...
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="text-[15px] text-foreground-700 leading-relaxed mb-8">
                {content.conclusion}
              </p>
            </div>

            <div className="border-t border-background-200 pt-10 mt-12">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <p className="text-[15px] font-heading text-foreground-950 font-semibold mb-2">
                    Ready to explore the itaobuy spreadsheet?
                  </p>
                  <p className="text-[12px] text-foreground-600">
                    Start comparing products across all 14 categories today.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-[13px] font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                  >
                    itaobuy sheet
                    <i className="ri-arrow-right-up-line text-xs"></i>
                  </a>
                  <Link
                    to="/"
                    className="px-6 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-[13px] font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Back to Homepage
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-12 bg-background-100 rounded-2xl p-8 text-center">
              <p className="text-[12px] text-foreground-500 mb-4">
                This article is part of the <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">itaobuy.hair</Link> SEO resource library.
                Explore more guides, comparisons, and tips on our homepage.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                <i className="ri-home-4-line"></i>
                Return to itaobuy.hair Homepage
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}